import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import monetateABTests from '../monetate-ab-tests'

export const MONETATE_ID_STORAGE_KEY = 'monetate-engine-id'
export const MONETATE_ID_COOKIE = 'mt.v'
export const SESSION_VALUE_STORAGE_PREFIX = 'monetate-abtest-session-'

const MTV_COOKIE_POLL_INTERVAL_MS = 50
const MTV_COOKIE_MAX_WAIT_MS = 2000

/** Query params that must not be sent to Monetate (auth/recovery links, tokens). */
const SENSITIVE_PAGE_VIEW_QUERY_PARAMS = new Set([
    'token',
    'email',
    'password',
    'code',
    'access_token',
    'refresh_token'
])

const runningTests = new Set()

export const getMonetateEnabled = () => {
    try {
        const {
            app: {commerceAPI}
        } = getConfig()
        const {config} = commerceAPI?.monetate || {}
        return config ? JSON.parse(config).enabled : false
    } catch {
        return false
    }
}

/**
 * Monetate browser-tag identity from the mt.v cookie (ticket: prefer this as monetateId).
 */
export const getMonetateIdFromMtvCookie = () => {
    if (isServer) {
        return null
    }

    try {
        const match = document.cookie.match(/(?:^|;\s*)mt\.v=([^;]*)/)
        if (!match?.[1]) {
            return null
        }
        const value = decodeURIComponent(match[1].trim())
        return value || null
    } catch {
        return null
    }
}

/**
 * Prefer mt.v when present; otherwise reuse Engine ID from localStorage (existing fallback).
 */
export const getMonetateIdForEngineRequest = () => {
    if (isServer) {
        return null
    }

    const fromCookie = getMonetateIdFromMtvCookie()
    if (fromCookie) {
        return fromCookie
    }

    try {
        return window.localStorage.getItem(MONETATE_ID_STORAGE_KEY) || null
    } catch {
        return null
    }
}

/**
 * Briefly wait for mt.v when the Monetate browser tag may still be initializing.
 * Skipped when mt.v or a persisted Engine fallback ID already exists.
 */
export const waitForMonetateIdFromCookie = (maxWaitMs = MTV_COOKIE_MAX_WAIT_MS) => {
    if (isServer) {
        return Promise.resolve(null)
    }

    const existing = getMonetateIdFromMtvCookie()
    if (existing) {
        return Promise.resolve(existing)
    }

    return new Promise((resolve) => {
        const start = Date.now()
        const interval = setInterval(() => {
            const id = getMonetateIdFromMtvCookie()
            if (id || Date.now() - start >= maxWaitMs) {
                clearInterval(interval)
                resolve(id)
            }
        }, MTV_COOKIE_POLL_INTERVAL_MS)
    })
}

/**
 * Page URL safe for third-party PageView context (no auth tokens or PII in query).
 */
export const buildSafeMonetatePageViewUrl = () => {
    if (isServer || !window.location) {
        return null
    }

    try {
        const {origin, pathname, search} = window.location
        if (!search) {
            return `${origin}${pathname}`
        }

        const params = new URLSearchParams(search)
        SENSITIVE_PAGE_VIEW_QUERY_PARAMS.forEach((key) => params.delete(key))
        const sanitized = params.toString()
        return sanitized ? `${origin}${pathname}?${sanitized}` : `${origin}${pathname}`
    } catch {
        return null
    }
}

const isValidExperimentValue = (value) => value === 'A' || value === 'B'

const getExperimentKeyForSessionKey = (sessionKey) =>
    monetateABTests.find((test) => test.sessionKey === sessionKey)?.experimentKey || null

/**
 * Expose SFCC-backed A/B assignment for LeanConvert (window.__KS_EXPERIMENTS__).
 */
export const publishKsExperiment = (experimentKey, value) => {
    if (isServer || !experimentKey || !isValidExperimentValue(value)) {
        return
    }

    window.__KS_EXPERIMENTS__ = {
        ...(window.__KS_EXPERIMENTS__ || {}),
        [experimentKey]: value
    }
}

export const publishKsExperimentForSessionKey = (sessionKey, value) => {
    const experimentKey = getExperimentKeyForSessionKey(sessionKey)
    if (experimentKey) {
        publishKsExperiment(experimentKey, value)
    }
}

/**
 * Hydrate LeanConvert contract from persisted Monetate session assignments.
 */
export const hydrateKsExperimentsFromStorage = () => {
    if (isServer) {
        return
    }

    monetateABTests.forEach((test) => {
        if (!test.experimentKey) {
            return
        }
        const value = getStoredSessionValue(test.sessionKey)
        if (value) {
            publishKsExperiment(test.experimentKey, value)
        }
    })
}

export const getStoredSessionValue = (sessionKey) => {
    if (!sessionKey || isServer) {
        return null
    }

    try {
        return window.localStorage.getItem(`${SESSION_VALUE_STORAGE_PREFIX}${sessionKey}`)
    } catch {
        return null
    }
}

export const storeSessionValue = (sessionKey, value) => {
    if (!sessionKey || !value || isServer) {
        return
    }

    try {
        window.localStorage.setItem(`${SESSION_VALUE_STORAGE_PREFIX}${sessionKey}`, value)
    } catch {
        // Ignore storage failures.
    }

    publishKsExperimentForSessionKey(sessionKey, value)
}

/**
 * Real shopper/browser context for Engine decide (ticket: UA + page URL).
 */
export const buildMonetateContextEvents = () => {
    if (isServer) {
        return []
    }

    const contextEvents = []

    try {
        if (navigator?.userAgent) {
            contextEvents.push({
                eventType: 'monetate:context:UserAgent',
                userAgent: navigator.userAgent
            })
        }
    } catch {
        // Ignore UA read failures.
    }

    try {
        const url = buildSafeMonetatePageViewUrl()
        if (url) {
            contextEvents.push({
                eventType: 'monetate:context:PageView',
                url
            })
        }
    } catch {
        // Ignore URL read failures.
    }

    return contextEvents
}

const runTestByCallingMonetate = async (test, {customerId} = {}) => {
    try {
        if (!getMonetateIdFromMtvCookie() && !getMonetateIdForEngineRequest()) {
            await waitForMonetateIdFromCookie()
        }

        const monetateIdFromCookie = getMonetateIdFromMtvCookie()
        const monetateId = getMonetateIdForEngineRequest()
        const requestId = `${test.id}-${Date.now()}-${
            window.crypto?.randomUUID?.() || Math.random().toString(36).slice(2)
        }`
        const requestBody = test.buildRequestBody({
            requestId,
            monetateId,
            // Authenticated only — never replaces monetateId.
            customerId: customerId || null,
            contextEvents: buildMonetateContextEvents()
        })

        const response = await fetch(test.endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestBody)
        })

        if (!response.ok) {
            return null
        }

        const decision = await response.json()
        const returnedMonetateId = decision?.meta?.monetateId

        // Only persist an Engine-minted ID when mt.v was not available (legacy fallback path).
        if (!monetateIdFromCookie && returnedMonetateId) {
            try {
                window.localStorage.setItem(MONETATE_ID_STORAGE_KEY, returnedMonetateId)
            } catch {
                // Ignore storage failures; request flow can continue.
            }
        }

        return test.getSessionValue(decision)
    } catch {
        return null
    }
}

/**
 * Resolves Monetate A/B test values for Shopper Context customQualifiers.
 * Reuses localStorage when already assigned; otherwise calls Monetate once per test.
 * @param {{customerId?: string|null}} [options] - Registered SFCC customer id when authenticated.
 */
export const resolveMonetateCustomQualifiers = async ({customerId} = {}) => {
    if (isServer || !getMonetateEnabled() || !monetateABTests.length) {
        return {}
    }

    const qualifiers = {}

    for (const test of monetateABTests) {
        const storedValue = getStoredSessionValue(test.sessionKey)
        if (storedValue) {
            qualifiers[test.sessionKey] = storedValue
            if (test.experimentKey) {
                publishKsExperiment(test.experimentKey, storedValue)
            }
            continue
        }

        if (runningTests.has(test.id)) {
            continue
        }

        runningTests.add(test.id)

        try {
            const sessionValue = await runTestByCallingMonetate(test, {customerId})
            if (sessionValue) {
                qualifiers[test.sessionKey] = sessionValue
                // __KS_EXPERIMENTS__ is published only after Shopper Context PUT succeeds.
            }
        } finally {
            runningTests.delete(test.id)
        }
    }

    return qualifiers
}
